import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, serverTimestamp, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import './Cities.css';

function Cities() {
  const [activeTab, setActiveTab] = useState('approved'); // 'approved' or 'requests'
  const [approvedCities, setApprovedCities] = useState([]);
  const [cityRequests, setCityRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    fetchApprovedCities();
    fetchCityRequests();
  }, []);

  const fetchApprovedCities = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Try fetching all cities first, then sort on client side
      // This avoids potential Firestore index issues
      const citiesQuery = collection(db, 'cities');
      
      const querySnapshot = await getDocs(citiesQuery);
      const citiesData = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        citiesData.push({
          id: doc.id,
          cityName: data.cityName || data.name || 'N/A',
          district: data.district || 'N/A',
          state: data.state || 'N/A',
          normalizedKey: data.normalizedKey || data.normalized_key || '',
          createdAt: data.createdAt
        });
      });
      
      // Sort on client side by cityName
      citiesData.sort((a, b) => a.cityName.localeCompare(b.cityName));
      
      setApprovedCities(citiesData);
    } catch (err) {
      setError('Failed to load approved cities. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCityRequests = async () => {
    try {
      setLoading(true);
      setError('');
      
      const requestsQuery = query(
        collection(db, 'city_requests'),
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(requestsQuery);
      const requestsData = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        requestsData.push({
          id: doc.id,
          cityName: data.cityName || 'N/A',
          district: data.district || 'N/A',
          state: data.state || 'N/A',
          normalizedKey: data.normalizedKey || '',
          requesterId: data.requesterId || '',
          requesterRole: data.requesterRole || '',
          requesterName: data.requesterName || 'N/A',
          requesterPhone: data.requesterPhone || 'N/A',
          status: data.status || 'pending',
          createdAt: data.createdAt
        });
      });
      
      setCityRequests(requestsData);
    } catch (err) {
      setError('Failed to load city requests. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (request) => {
    const confirmed = window.confirm(
      `Are you sure you want to approve the city request for "${request.cityName}"?\n\nThis will create the city in the approved list and notify the salon owner.`
    );

    if (!confirmed) return;

    try {
      setActionLoading(true);
      setError('');

      const requestRef = doc(db, 'city_requests', request.id);
      await updateDoc(requestRef, {
        status: 'approved',
        updatedAt: serverTimestamp(),
        approvedAt: serverTimestamp()
      });

      // Wait a moment for Cloud Function to create the city
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Refresh both lists
      await fetchCityRequests();
      await fetchApprovedCities();

      alert(`City request for "${request.cityName}" has been approved successfully! The city has been added to the approved list.`);
    } catch (err) {
      alert(`Failed to approve city request: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectClick = (request) => {
    setSelectedRequest(request);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const handleRejectSubmit = async () => {
    if (!rejectionReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }

    try {
      setActionLoading(true);
      setError('');

      const requestRef = doc(db, 'city_requests', selectedRequest.id);
      await updateDoc(requestRef, {
        status: 'rejected',
        rejectionReason: rejectionReason.trim(),
        updatedAt: serverTimestamp(),
        rejectedAt: serverTimestamp()
      });

      // Close modal and refresh list
      setShowRejectModal(false);
      setSelectedRequest(null);
      setRejectionReason('');
      
      await fetchCityRequests();

      alert(`City request for "${selectedRequest.cityName}" has been rejected. The salon owner will be notified.`);
    } catch (err) {
      alert(`Failed to reject city request: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchApprovedCities();
    fetchCityRequests();
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (err) {
      return 'N/A';
    }
  };

  if (error && !approvedCities.length && !cityRequests.length) {
    return (
      <div className="cities-page">
        <div className="error-container">
          <p className="error-text">{error}</p>
          <button onClick={handleRefresh} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cities-page">
      <div className="page-header">
        <div className="header-info">
          <h2>City Management</h2>
          <p>Manage approved cities and review city requests from salons</p>
        </div>
        <button onClick={handleRefresh} className="refresh-btn" disabled={loading}>
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <button
          className={`tab-btn ${activeTab === 'approved' ? 'active' : ''}`}
          onClick={() => setActiveTab('approved')}
        >
          Approved Cities ({approvedCities.length})
        </button>
        <button
          className={`tab-btn ${activeTab === 'requests' ? 'active' : ''}`}
          onClick={() => setActiveTab('requests')}
        >
          Pending Requests ({cityRequests.length})
        </button>
      </div>

      {/* Stats */}
      <div className="stats-container">
        <div className="stat-card">
          <h3>Total Approved Cities</h3>
          <p className="stat-number">{approvedCities.length}</p>
        </div>
        <div className="stat-card">
          <h3>Pending Requests</h3>
          <p className="stat-number" style={{ color: '#f59e0b' }}>{cityRequests.length}</p>
        </div>
      </div>

      {/* Content */}
      {loading && approvedCities.length === 0 && cityRequests.length === 0 ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading data...</p>
        </div>
      ) : (
        <>
          {/* Approved Cities Tab */}
          {activeTab === 'approved' && (
            <div className="content-section">
              <h3 className="section-title">Approved Cities</h3>
              {approvedCities.length === 0 ? (
                <div className="empty-state">
                  <p>No approved cities found</p>
                </div>
              ) : (
                <div className="cities-grid">
                  {approvedCities.map((city) => (
                    <div key={city.id} className="city-card approved">
                      <div className="city-header">
                        <h4>{city.cityName}</h4>
                        <span className="status-badge status-approved">Approved</span>
                      </div>
                      <div className="city-details">
                        <div className="detail-row">
                          <span className="detail-label">District:</span>
                          <span className="detail-value">{city.district}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">State:</span>
                          <span className="detail-value">{city.state}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Key:</span>
                          <span className="detail-value normalized-key">{city.normalizedKey}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Pending Requests Tab */}
          {activeTab === 'requests' && (
            <div className="content-section">
              <h3 className="section-title">Pending City Requests</h3>
              {cityRequests.length === 0 ? (
                <div className="empty-state">
                  <p>No pending city requests</p>
                  <small>When salon owners request new cities, they will appear here for approval</small>
                </div>
              ) : (
                <div className="requests-list">
                  {cityRequests.map((request) => (
                    <div key={request.id} className="request-card">
                      <div className="request-header">
                        <div className="request-city-info">
                          <h4>{request.cityName}</h4>
                          <span className="status-badge status-pending">Pending</span>
                        </div>
                        <span className="request-date">{formatDate(request.createdAt)}</span>
                      </div>

                      <div className="request-details">
                        <div className="location-info">
                          <div className="detail-row">
                            <span className="detail-label">📍 District:</span>
                            <span className="detail-value">{request.district}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">📍 State:</span>
                            <span className="detail-value">{request.state}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">🔑 Key:</span>
                            <span className="detail-value normalized-key">{request.normalizedKey}</span>
                          </div>
                        </div>

                        <div className="requester-info">
                          <h5>Requested By:</h5>
                          <div className="detail-row">
                            <span className="detail-label">Salon:</span>
                            <span className="detail-value">{request.requesterName}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Phone:</span>
                            <span className="detail-value">{request.requesterPhone}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Role:</span>
                            <span className="detail-value">{request.requesterRole}</span>
                          </div>
                        </div>
                      </div>

                      <div className="request-actions">
                        <button
                          className="approve-btn"
                          onClick={() => handleApprove(request)}
                          disabled={actionLoading}
                        >
                          ✓ Approve
                        </button>
                        <button
                          className="reject-btn"
                          onClick={() => handleRejectClick(request)}
                          disabled={actionLoading}
                        >
                          ✕ Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="modal-overlay" onClick={() => !actionLoading && setShowRejectModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reject City Request</h3>
              <button
                className="modal-close"
                onClick={() => setShowRejectModal(false)}
                disabled={actionLoading}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <p className="modal-city-name">
                City: <strong>{selectedRequest?.cityName}</strong>
              </p>
              <p className="modal-description">
                Please provide a reason for rejecting this city request. The salon owner will receive this message.
              </p>

              <label htmlFor="rejection-reason">Rejection Reason *</label>
              <textarea
                id="rejection-reason"
                className="rejection-textarea"
                placeholder="e.g., City name is incorrect, duplicate request, service area not covered..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={4}
                disabled={actionLoading}
                required
              />
            </div>

            <div className="modal-footer">
              <button
                className="modal-cancel-btn"
                onClick={() => setShowRejectModal(false)}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                className="modal-submit-btn"
                onClick={handleRejectSubmit}
                disabled={actionLoading || !rejectionReason.trim()}
              >
                {actionLoading ? 'Submitting...' : 'Submit Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Cities;

